import { useEffect } from 'react';
import { useUpdate } from 'react-use';

import InfoGlyph from '~/modules/elements/info-glyph';
import MenuWithLogo from '~/modules/elements/menu-with-logo';
import { NavButton, NavCheckbox } from '~/modules/elements/nav-controls';
import events from '~/modules/game-events/game-events';
import { useEventListenerSelector } from '~/modules/game-events/hooks';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import { GAME_CODE_LENGTH, storeGameCode } from '~/modules/remote-mic/network/server/network-server';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import { nextValue } from '~/modules/utils/indexes';
import { ControlGroup } from '~/routes/keyboard-help/controls';
import { PanelLabel } from '~/routes/profiles/profile-panels';
import { useDevicePing } from '~/routes/select-input/hooks/use-device-ping';
import PhoneLinkAddressField from '~/routes/settings/phone-link-address-field';
import { NavSettingsRow, SettingsCard, SettingsSection } from '~/routes/settings/settings-rows';
import {
  DefaultRemoteMicPermission,
  RemoteMicConnectionType,
  RemoteMicConnectionTypeSetting,
  RemoteMicPermissions,
  UnassignOnSongFinishedSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';
import { domNavProps } from '~/routes/singers/nav-props';

const REMOTE_MIC_CARD = 'sm:max-w-[min(94vw,100rem)] lg:max-w-[min(94vw,100rem)] 2xl:max-w-[min(94vw,100rem)]';

/** Wider aside than Settings: a device row carries an id, a ping, a name and a permission. */
const SPLIT_WIDE = 'min-[60rem]:[grid-template-columns:minmax(0,1fr)_28rem]';

const PERMISSION_INFO =
  'WRITE - player is able to navigate the menus remotely and assign themselves and other players to the game. READ - player will only be able to use the device as a microphone with no control over the game.';

const GHOST_PLAYERS_INFO = 'Prevents "ghost" players - remote mics sticking around, not singing subsequent songs.';

/**
 * The phone gets the screen's own three sections rather than one flat list: `Connection` and
 * `Players` are the two `SettingsSection` labels the card already draws, and `Connected devices` the
 * panel label beside them. Both also register from the rows that draw them (`NavSettingsRow`) rather
 * than from the component body, which ran before any JSX did and so put them above the back row -
 * mid-list on the phone, and in an arrow order the television never shows.
 */
const CONNECTION_GROUP: ControlGroup = { id: 'remote-mic-connection', label: 'Connection' };
const PLAYERS_GROUP: ControlGroup = { id: 'remote-mic-players', label: 'Players' };

const PHONE_LINK_INFO =
  'Where the QR code sends the phone. The phone never connects to this computer directly - both ends meet on the relay server using the room code, so any address serving the game works, on your Wi-Fi or not. Leave it empty for AUTO, which uses the address the game itself is served from unless that is a loopback one - localhost and 127.0.0.1 mean the phone itself once the phone reads them, so the desktop app and the dev server fall back to a public address instead.';

function RemoteMicSettings() {
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const goBack = () => navigate('settings/');

  const { register } = useKeyboardNav({ onBackspace: goBack, title: 'Remote Microphone Settings' });

  const [remoteMicConnectionType, setRemoteMicConnectionType] = useSettingValue(RemoteMicConnectionTypeSetting);
  const [defaultPermission, setDefaultPermission] = useSettingValue(DefaultRemoteMicPermission);
  const [unassignOnSongFinished, setUnassignOnSongFinished] = useSettingValue(UnassignOnSongFinishedSetting);
  const remoteMics = useEventListenerSelector(events.inputListChanged, () => RemoteMicManager.getRemoteMics());

  const forceUpdate = useUpdate();
  useEffect(() => RemoteMicManager.addListener(forceUpdate), [forceUpdate]);

  // Kept from the old header: typing a code here joins an existing room. Nothing on screen says so
  // on purpose - it is a support affordance, not a setting.
  const askForGameCode = () => {
    const code = prompt('Code');

    if (code?.length === GAME_CODE_LENGTH - 1) {
      storeGameCode(code);

      global.location.reload();
    }
  };

  return (
    <MenuWithLogo centered spacing="bare" className={REMOTE_MIC_CARD} data-test="remote-mic-settings-page">
      <KeyboardNavContext value={register}>
        <div className="flex flex-wrap items-center gap-[1.125rem]">
          <NavButton
            name="back-button"
            variant="back"
            size="small"
            className="h-[3.375rem] w-auto px-5"
            remoteLabel="Return To Settings"
            onClick={goBack}>
            ← Settings
          </NavButton>
          <span
            className="stroke-text typography text-active cursor-pointer text-[2.125rem] font-bold"
            onClick={askForGameCode}>
            Remote Microphones
          </span>
        </div>

        <SettingsCard className={SPLIT_WIDE}>
          <div className="flex min-w-0 flex-col gap-[1.375rem]">
            <SettingsSection label="Connection">
              <NavSettingsRow
                name="connection type"
                onClick={() => setRemoteMicConnectionType(nextValue(RemoteMicConnectionType, remoteMicConnectionType))}
                help="Connection type"
                isDefault
                control={{
                  type: 'switch',
                  label: 'Connection type',
                  value: remoteMicConnectionType,
                  group: CONNECTION_GROUP,
                }}
                label="Connection type"
                value={remoteMicConnectionType}
                data-test-value={remoteMicConnectionType}
              />
              <PhoneLinkAddressField
                register={register}
                name="remote-mic-origin"
                label={
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    Phone link address
                    <InfoGlyph text={PHONE_LINK_INFO} />
                  </span>
                }
                remoteLabel="Phone link address"
                group={CONNECTION_GROUP}
              />
            </SettingsSection>

            <SettingsSection label="Players">
              <NavSettingsRow
                name="default-permission"
                onClick={() => setDefaultPermission(nextValue(RemoteMicPermissions, defaultPermission))}
                help="Default permission"
                control={{
                  type: 'switch',
                  label: 'Default permission',
                  value: defaultPermission.toUpperCase(),
                  group: PLAYERS_GROUP,
                }}
                label="Default permission"
                value={defaultPermission.toUpperCase()}
                data-test-value={defaultPermission.toUpperCase()}
                info={PERMISSION_INFO}
              />
              <NavCheckbox
                size="small"
                name="unassign-on-song-finished"
                group={PLAYERS_GROUP}
                label="Unassign players after they finish singing"
                remoteLabel="Unassign after singing"
                className="h-[3.125rem]"
                checked={unassignOnSongFinished}
                onClick={() => setUnassignOnSongFinished(!unassignOnSongFinished)}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">Unassign after singing</span>
                  <InfoGlyph text={GHOST_PLAYERS_INFO} />
                </span>
              </NavCheckbox>
            </SettingsSection>
          </div>

          <div className="flex min-w-0 flex-col gap-2.5">
            <div className="flex items-baseline justify-between">
              <PanelLabel>Connected devices</PanelLabel>
              <span className="text-[0.9375rem] text-white/35 tabular-nums">{remoteMics.length}</span>
            </div>
            {remoteMics.map((mic) => {
              const permission = RemoteMicManager.getPermission(mic.id);
              const navProps = register(
                `mic-${mic.id}`,
                () => RemoteMicManager.setPermission(mic.id, nextValue(RemoteMicPermissions, permission)),
                'Change permissions',
                false,
                {
                  control: {
                    type: 'switch',
                    label: mic.name,
                    value: permission,
                    // A device count, not a song total - the panel beside the list already shows it.
                    group: { id: 'remote-mic-devices', label: `Connected devices \u00b7 ${remoteMics.length}` },
                  },
                },
              );

              return (
                <button
                  key={mic.id}
                  type="button"
                  {...domNavProps(navProps)}
                  data-test="remote-mic-entry"
                  data-id={mic.id}
                  className={`shadow-focusable flex h-[3.375rem] cursor-pointer items-center gap-3 rounded bg-black/55 px-4 ${
                    navProps.focused ? 'subtle-focus' : ''
                  }`}>
                  <span className="shrink-0 font-mono text-base text-white/40 uppercase">{mic.id.slice(-4)}</span>
                  <span className="w-[3.5rem] shrink-0 text-left text-base text-white/40 tabular-nums">
                    <DevicePing deviceId={mic.id} />
                  </span>
                  <span className="ph-no-capture min-w-0 flex-1 truncate text-left text-[1.25rem]">{mic.name}</span>
                  <span className="text-active shrink-0 text-[1.25rem] font-bold uppercase">{permission}</span>
                </button>
              );
            })}
            {remoteMics.length === 0 && (
              <span className="text-[1.0625rem] text-white/40">No remote microphones connected</span>
            )}
          </div>
        </SettingsCard>
      </KeyboardNavContext>
    </MenuWithLogo>
  );
}
const DevicePing = ({ deviceId }: { deviceId: string }) => {
  const latency = useDevicePing(deviceId);

  return latency === null ? null : <>{latency}ms</>;
};

export default RemoteMicSettings;
